import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import '../../styles/sitePages.css';
import './AppDetailPage.css';
import { appStoreUrl, playStoreUrl, trackStoreClick } from '../../utils/storeLinks';

function DotStreamAppPage() {
  const reviews = [
    {
      author: 'Path Finder',
      rating: '★★★★★',
      text: "Addictive and satisfying! Finding the perfect path through all the dots is so rewarding. Great puzzle design.",
      source: 'App Store'
    },
    {
      author: 'Logic Master',
      rating: '★★★★★',
      text: "Clean, simple, and challenging. This is what a puzzle game should be. No fluff, just great puzzles.",
      source: 'Google Play'
    },
    {
      author: 'Daily Player',
      rating: '★★★★☆',
      text: "Perfect for a quick brain workout. The numbered dot mechanic adds an interesting twist to path-finding puzzles.",
      source: 'App Store'
    }
  ];

  const features = [
    { title: 'Connect the Dots', description: 'Draw a continuous path connecting all numbered dots in sequence.' },
    { title: 'Fill Every Cell', description: 'Your path must visit every cell on the grid exactly once.' },
    { title: 'Unique Solutions', description: 'Each puzzle has one perfect solution discoverable through logic.' },
    { title: 'Progressive Challenge', description: 'Start with simple grids and advance to complex multi-path puzzles.' },
    { title: 'Clean Neon Aesthetic', description: 'Beautiful minimalist design with satisfying visual feedback.' },
    { title: 'Hundreds of Levels', description: 'Hand-crafted puzzles to keep you engaged for hours.' }
  ];

  return (
    <div className="app-detail-page">
      <Helmet>
        <title>Dot Stream Logic Puzzle - Mobile App for iOS & Android | Logic Puzzles</title>
        <meta name="description" content="Download Dot Stream for iOS and Android. 4.7-star App Store rating. Connect numbered dots into one continuous path in this satisfying logic puzzle game." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps/dotstream" />
        <meta property="og:title" content="Dot Stream Logic Puzzle - Mobile App for iOS & Android" />
        <meta property="og:description" content="Download Dot Stream. 4.7-star App Store rating. Connect numbered dots into one continuous path." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps/dotstream" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dot Stream Logic Puzzle - Mobile App for iOS & Android" />
        <meta name="twitter:description" content="Download Dot Stream. 4.7-star App Store rating. Connect numbered dots into one continuous path." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>

      <NavBar />

      <section className="app-detail-hero">
        <div className="container app-detail-hero-content">
          <div className="app-detail-hero-copy">
            <span className="eyebrow">Mobile app · iOS &amp; Android</span>
            <h1>Dot Stream</h1>
            <p className="app-tagline">Connect dots into one perfect path</p>

            <div className="app-detail-stats">
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">4.7 ★</span>
                <span className="app-detail-stat-label">Rating</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">3K+</span>
                <span className="app-detail-stat-label">Downloads</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">300+</span>
                <span className="app-detail-stat-label">Levels</span>
              </div>
            </div>

            <div className="app-detail-buttons">
              <a href={appStoreUrl('dotstream', 'apps-page', '/apps/dotstream')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'dotstream', store: 'app_store', placement: 'apps-page', page: '/apps/dotstream' })}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
              <a href={playStoreUrl('dotstream', 'apps-page', '/apps/dotstream')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'dotstream', store: 'google_play', placement: 'apps-page', page: '/apps/dotstream' })}>
                <img src="/play-store-badge.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-detail-hero-art">
            <img
              src="/assets/logos/dotstream_logo.webp"
              alt="Dot Stream App Icon"
              className="app-detail-icon"
              width="200"
              height="200"
            />
          </div>
        </div>
      </section>

      <div className="app-detail-content">
        <section className="app-detail-section">
          <h2>About Dot Stream</h2>
          <p className="app-detail-description">
            Dot Stream is a logic puzzle where you connect numbered dots into one continuous path. 
            Draw a line that visits every cell on the grid exactly once, passing through the numbered dots in order. 
            Simple rules, elegant solutions, and deeply satisfying solves.
          </p>
        </section>

        <section className="app-detail-section">
          <h2>Features</h2>
          <div className="app-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="app-feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>How to Play Dot Stream</h2>
          <p className="app-detail-description">
            Each puzzle shows a grid with numbered dots placed in certain cells. Draw a single continuous path
            that starts at dot 1, passes through every numbered dot in order, and visits every cell on the grid
            exactly once. The path can only move horizontally or vertically between adjacent cells. Finding the
            one path that satisfies all the constraints is a deeply satisfying logic challenge.
          </p>
          <h3>Tips for New Players</h3>
          <ul className="app-detail-list">
            <li>Work from both ends of the path - connecting from dot 1 forward and from the last dot backward can reveal forced moves.</li>
            <li>Corner and edge cells have fewer neighbors, so they often have only one way to be reached or exited.</li>
            <li>If a cell has only two unvisited neighbors, the path through it is fully determined.</li>
            <li>On larger grids, focus on areas near the numbered dots first to establish the path's general shape.</li>
          </ul>
        </section>

        <section className="app-detail-section app-reviews-section">
          <h2>What Players Are Saying</h2>
          <div className="app-reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="app-review-card">
                <div className="app-review-header">
                  <span className="app-review-author">{review.author}</span>
                  <span className="app-review-rating">{review.rating}</span>
                </div>
                <p className="app-review-text">"{review.text}"</p>
                <span className="app-review-source">{review.source}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section app-cta-section sp-cta">
          <div>
            <h2>Ready to Play?</h2>
            <p>Download Dot Stream and start connecting!</p>
          </div>
          <div className="app-cta-buttons sp-cta__actions">
            <Link to="/play/dotstream" className="button spark">
              Play Free Online
            </Link>
            <Link to="/apps" className="button sp-on-dark">
              ← Back to All Apps
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default DotStreamAppPage;
